import React, { useEffect, useState } from "react";
import styles from "./SalesforceFilterChips.module.css";

/**
 * Read-only filter chips that show the access-control filters automatically
 * applied for Salesforce-authenticated users. There are two kinds of chips:
 *
 *   - One "vendor" chip (e.g. "HolzHer") corresponding to the
 *     vendorApproval=true restriction the backend enforces.
 *   - One chip per Product_Group__c value (e.g. "Edgebander", "ProfiPress").
 *
 * These are *informational* — users can't remove them because they reflect
 * server-enforced access control, not user-chosen filters. They're rendered
 * alongside the existing user-selectable filter UI on chat / search pages.
 *
 * For Microsoft Entra users the component renders nothing.
 */
type SalesforceMeResponse = {
    authenticated: boolean;
    provider?: string;
    product_groups?: string[];
    vendor_label?: string;
};

const SALESFORCE_ME_URL = "/api/auth/salesforce/me";

export const SalesforceFilterChips: React.FC = () => {
    const [data, setData] = useState<SalesforceMeResponse | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetch(SALESFORCE_ME_URL, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
                if (cancelled) return;
                if (json && json.authenticated && json.provider === "salesforce") {
                    setData(json);
                }
            })
            .catch(() => {
                /* not authenticated via SF - render nothing */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!data) return null;

    const groups = data.product_groups ?? [];
    const vendor = data.vendor_label;

    if (!vendor && groups.length === 0) return null;

    return (
        <div className={styles.container} aria-label="Active access filters">
            {vendor && (
                <span
                    className={`${styles.chip} ${styles.chipVendor}`}
                    title="Vendor approval — applied automatically based on your login"
                >
                    {vendor}
                </span>
            )}
            {groups.map((g) => (
                <span
                    key={g}
                    className={`${styles.chip} ${styles.chipGroup}`}
                    title="Product group — from your Salesforce profile"
                >
                    {g}
                </span>
            ))}
        </div>
    );
};

export default SalesforceFilterChips;
